import {ChevronDown} from 'lucide-react'

/**
 * Shared field primitives for the composer's listing forms (class / job /
 * space). The three forms should read identically: same labels, same
 * inputs, and the guided "step 1, step 2" structure coming from <Section>
 * rather than per-form wrapper cards.
 */

// Base styling without a width so callers can compose `w-full` for
// full-row inputs or a fixed width for inline elements (e.g. the space
// rate-unit select).
// `leading-5` is explicit because selects don't inherit text-sm's line
// height, which left them a pixel off the inputs beside them.
export const baseInputClass =
  'px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm leading-5 text-sharpenn-black placeholder:text-green-gray focus:outline-none focus:border-sharpenn-coral focus:ring-2 focus:ring-sharpenn-coral/20 transition-colors'
export const inputClass = `${baseInputClass} w-full`

/**
 * A native select drawn like the other fields, with the same chevron as the
 * role picker instead of the browser's own arrow. `className` sizes the
 * wrapper (w-full, w-36 flex-shrink-0, ...).
 */
export const Select = ({className = 'w-full', children, ...props}) => (
  <div className={`relative ${className}`}>
    <select
      {...props}
      className={`${baseInputClass} w-full appearance-none pr-9 cursor-pointer`}
    >
      {children}
    </select>
    <ChevronDown
      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sharpenn-slate"
      aria-hidden
    />
  </div>
)

export const Label = ({children, optional}) => (
  <span className="block text-xs font-semibold uppercase tracking-wide text-sharpenn-slate mb-1.5">
    {children}
    {optional && (
      <span className="ml-1 text-green-gray font-normal normal-case">
        (optional)
      </span>
    )}
  </span>
)

/**
 * Numbered form section: a small coral step badge + title, with the
 * section's fields indented underneath. Sections stack with generous
 * breathing room instead of dividers or nested cards.
 */
export const Section = ({step, title, children}) => (
  <section className="mt-6 first:mt-0">
    <div className="flex items-center gap-2">
      <span className="w-6 h-6 rounded-full bg-sharpenn-blush text-sharpenn-coral text-xs font-bold flex items-center justify-center flex-shrink-0">
        {step}
      </span>
      <h3 className="text-sm font-semibold text-sharpenn-charcoal">{title}</h3>
    </div>
    <div className="mt-3 pl-8 space-y-4">{children}</div>
  </section>
)

/**
 * Compact chip row for small option sets (skill levels, work types,
 * space types, compensation). Tighter than onboarding's ChipSelector:
 * pill chips that wrap instead of a chunky grid.
 *
 * Same contract as ChipSelector where it matters: `selected` is an array
 * of value strings, `onChange` receives the next array. Options may be
 * `{name, value}` objects or plain strings (compensationTypes).
 */
export const CompactChips = ({
  options = [],
  selected = [],
  onChange,
  multiple = false,
}) => {
  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else if (multiple) {
      onChange([...selected, value])
    } else {
      onChange([value])
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const value = typeof option === 'string' ? option : option.value
        const label =
          typeof option === 'string'
            ? option.charAt(0).toUpperCase() +
              option.slice(1).replace(/_/g, ' ')
            : option.name
        const isSelected = selected.includes(value)
        return (
          <button
            key={value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => toggle(value)}
            className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
              isSelected
                ? 'bg-sharpenn-coral text-white border-transparent font-medium'
                : 'border-gray-200 text-sharpenn-slate hover:border-sharpenn-coral/40 hover:bg-sharpenn-cream'
            }`}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
